// ============================================================================
// Shared translation helper for the assessment wizards.
// ============================================================================
// Both the OINP and CRS assessments need the same behaviour, and the important
// part is the FALLBACK GRANULARITY: it falls back to English PER KEY, not per
// locale. That is what makes a partial locale safe to ship — a locale can carry
// only the handful of strings that have been human-checked, and every other key
// silently uses English rather than showing a raw key or an empty label.
//
// Which matters here more than usual: these wizards state eligibility criteria
// (CLB minimums, credential definitions, work-hour thresholds). Machine
// translating those is how someone acts on a wrong rule, so substantive copy
// stays English until reviewed while navigation and form chrome get translated.
// ============================================================================

const MONEY_PARAMS = /revenueFloor|amount|wage|target/i;

function formatParam(name, value) {
  if (typeof value === 'number' && MONEY_PARAMS.test(name)) {
    return `$${value.toLocaleString('en-CA')}`;
  }
  return String(value);
}

/**
 * Build a translator over a locale table.
 *
 * @param {Record<string, Record<string, string>>} locales keyed by language code
 * @param {string} fallback locale code used when a key is missing (default 'en')
 * @returns {{ t: Function, translator: Function, supported: string[] }}
 */
export function createI18n(locales, fallback = 'en') {
  const base = locales[fallback] || {};

  const t = (language, key, params) => {
    const locale = locales[language] || base;
    const template = locale[key] !== undefined ? locale[key] : base[key];
    if (template === undefined) return key;
    if (!params) return template;
    return String(template).replace(/\{(\w+)\}/g, (match, name) => (
      params[name] !== undefined ? formatParam(name, params[name]) : match
    ));
  };

  return {
    t,
    translator: (language) => (key, params) => t(language, key, params),
    supported: Object.keys(locales),
  };
}
